<template>
  <div class="d-flex flex-column mh-100 h-100 sunburst-container">
    <div class="w-100 d-flex">
      <font-awesome-icon
        :color="upArrowColor"
        class="m-auto"
        :class="{'opacity-0': isRoot, 'cursor-pointer': !isRoot }"
        icon="arrow-alt-circle-up"
        size="4x"
        @click="storyPathUp"
      />
    </div>
    <div class="mh-100 h-100 minimum-height">
      <svg
        id="sunburst"
        :viewBox="viewBox"
      >
        <!-- The Sunburst Chart SVG -->
      </svg>
    </div>
  </div>
</template>

<script lang="ts">
// TODO: remove @ts-nocheck
// @ts-nocheck

import Vue from 'vue'
import Component from 'vue-class-component'

import { partition, hierarchy } from 'd3-hierarchy'
import { select } from 'd3-selection'
import { arc } from 'd3-shape'
import { group } from 'd3-array'
import { interpolate } from 'd3-interpolate'
import 'd3-transition'
import { StoryKeys } from '@/types'
import {
  PATH_ROOT,
  VISUALISATION_VIEWBOX_POSITION_OFFSET,
  VISUALISATION_VIEWBOX_SIZE,
} from '@/constants'

// Potentially customer edited in the future
const padding = 20
const maxFontSize = 5
const minFontSize = 0.5
const maxChars = 20
const radius = 200
const rTotal = 800
const largeArcConstant = 0.03

// eslint-disable-next-line no-use-before-define
@Component<Sunburst>({
  watch: {
    currentStoryPath () {
      this.updateSunburst()
    },

    builderChangeUpdate () {
      // There is room for performance improvements here, by changing specific aspects of
      // the sunburst, rather than updating or recreating the entire chart
      this.updateSunburst()
    },

    builderChangeRebuild () {
      // Some aspects are set during creation, so require rebuild
      this.removeSunburst()
      this.createSunburst()
    },

    tabLabels () {
      // As there is no debounce on the tab labels, this watcher will be called on every click
      // therefore (unlike the others) it has been given a specific update function
      this.updateTabLabels()
    },

  },
})
export default class Sunburst extends Vue {
  PATH_ROOT = PATH_ROOT

  mounted () {
    this.createSunburst()
  }

  get viewBox () {
    return `${VISUALISATION_VIEWBOX_POSITION_OFFSET} ${VISUALISATION_VIEWBOX_POSITION_OFFSET} ${VISUALISATION_VIEWBOX_SIZE} ${VISUALISATION_VIEWBOX_SIZE}`
  }

  get levels () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.levels
  }

  get projectLabelField () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.projectLabelField
  }

  get maximum () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.sunburst.max
  }

  get label () {
    return this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.label
  }

  get name () {
    return this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.name
  }

  get order () {
    return this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.order
  }

  get colour () {
    return this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.colour
  }

  get d3data () {
    return this.$typedStore.visualisationData.d3data
  }

  get borderWidth () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.hollowBubbles.borderWidth
  }

  get tabFontColor () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.tabFontColor
  }

  get currentStoryPath () {
    if (this.$typedStore.activeVisualisation.isPrimaryApp) {
      return this.$typedStore.public.display.storyPath.toString()
    } else {
      return this.$typedStore.public.display.secondaryStoryPath.toString()
    }
  }

  get isRoot () {
    return this.currentStoryPath === PATH_ROOT
  }

  get rootHeight () {
    return this.hierarchyData.height
  }

  get tabPaths () {
    return [
      { storyPath: PATH_ROOT, id: this.idFromStoryPath(PATH_ROOT) },
      ...this.hierarchyData.children.map(({ storyPath, id }) => ({ storyPath, id })),
    ]
  }

  get tabLabels () {
    return this.tabPaths.map(({ storyPath, id }) => ({
      storyPath,
      id,
      label: this.label(storyPath) || this.name(storyPath),
    }))
  }

  get tabColor () {
    // Only used to trigger the watcher, so keep as a simple array of strings
    return this.tabPaths.map(({ storyPath }) => [
      this.colour(storyPath),
    ])
  }

  get tabPathsFontColor () {
    // Only used to trigger the watcher, so keep as a simple array of strings
    return this.tabPaths.map(({ storyPath }) => [
      this.tabFontColor(storyPath, StoryKeys.SIDEBAR_BOT),
    ])
  }

  get tabOrder () {
    // Only used to trigger the watcher, so keep as a simple array of numbers
    return this.tabPaths.map(({ storyPath }) => this.order(storyPath))
  }

  get upArrowColor () {
    return this.colour(PATH_ROOT)
  }

  get groupedData () {
    // Turn the data into a nested Map
    // https://github.com/d3/d3-array/blob/v3.0.2/README.md#group
    return group(
      this.d3data,
      (d) => d.data[this.levels.columnForLevel(1)],
      (d) => d.data[this.levels.columnForLevel(2)],
    )
  }

  get hierarchyData () {
    // Create a root node for the hierarchy
    // https://github.com/d3/d3-hierarchy/blob/v3.0.1/README.md#hierarchy
    return hierarchy(this.groupedData)
      .sum((data) => data.scaledTotal >= 0 ? data.scaledTotal : 0)
      .each((d) => {
        // Write some static data to avoid recalulating every time
        d.storyPath = this.storyPathFromD(d) // using `storyPath` to avoid conflict with hierarchy's `path` function and svg `path` element
        d.id = this.idFromStoryPath(d.storyPath)
      })
      .sort((a, b) => {
        if (a.depth === 1 && b.depth === 1) {
          // Sort level 1 according to tab order
          return this.order(a.storyPath) - this.order(b.storyPath)
        }
        // Sort all other tabs descending by size
        return b.value - a.value
      })
  }

  get partitionData () {
    // Partition the data to create adjacency diagram ie calculate x0, x1, y0, y1
    // Note x0, x1, y0, y1 do not change with animations. They always hold the positions for the complete adjacency diagram
    // https://github.com/d3/d3-hierarchy/blob/v3.0.1/README.md#partition
    return partition().size([2 * Math.PI, this.hierarchyData.height + 1])(
      this.hierarchyData,
    )
  }

  get builderChangeUpdate () {
    // Combined computed to use in watcher
    return [
      this.maximum,
    ]
  }

  get builderChangeRebuild () {
    // Combined computed to use in watcher
    return [
      this.tabColor,
      this.tabPathsFontColor,
      this.tabOrder,
      this.projectLabelField,
    ]
  }

  removeSunburst () {
    select('svg#sunburst').selectAll('g').remove()
  }

  createSunburst () {
    const svg = select('svg#sunburst')

    this.calculateTarget()
    // Store targets as initial current to ensure smooth first animation
    this.partitionData.each(d => { d.current = d.target })

    const data = this.partitionData.descendants()

    // One group per segment, with common attributes attached
    const g = svg.selectAll('g')
      .data(data, (d) => d.id)
      .join('g')
      .attr('id', (d) => d.id)
      .attr('class', (d) => d.target.display ? 'cursor-pointer' : 'pointer-events-none')
      .on('click', (_, d) => { this.$typedStore.activeVisualisation.updateStoryPath(d.storyPath) })

    // Arc segments
    g.append('path')
      .attr('class', 'arc-path')
      .attr('d', (d) => this.arcPath(d.current))
      .attr('fill', (d) => this.color(d))
      .attr('fill-opacity', (d) => this.fillOpacity(d))
      .attr('stroke', (d) => this.color(d))
      .attr('stroke-width', (d) => d.storyPath === this.currentStoryPath ? this.borderWidth : 0)

    // Labels
    g
      .append('path')
      .attr('id', (d) => `${d.id}--textPath`)
      .attr('class', 'label-path')
      .attr('fill', 'none')
      .attr('d', (d) => this.textPositionPath(d.current))

    g
      .append('text')
      .attr('fill', (d) => this.fontColor(d))
      .attr('fill-opacity', (d) => this.fontOpacity(d))
      .append('textPath')
      .attr('startOffset', '50%')
      .attr('dominant-baseline', 'central')
      .attr('text-anchor', 'middle')
      .attr('href', (d) => `#${d.id}--textPath`)
      .text(d => this.textRestrictedLength(d))

    // Update label size after text added (large arcs only)
    g.select('text')
      .style('font-size', d => this.largeArc(d.current) ? this.textFontSize(d) : 0)
  }

  updateSunburst () {
    const svg = select('svg#sunburst')

    this.calculateTarget()

    // Create a transition
    const t = svg
      .transition()
      .duration(750)

    const g = t
      .selectAll('g')
      // Interpolate from d.current to d.target
      // d.current is updated on each step, so use d.current in animations
      // End state of d.current will be d.target
      .attr('class', (d) => d.target.display ? 'cursor-pointer' : 'pointer-events-none')
      .tween('data', d => {
        const i = interpolate(d.current, d.target)
        return t => { d.current = i(t) }
      })

    g.select('path.arc-path')
      .attr('fill-opacity', (d) => this.fillOpacity(d))
      .attrTween('d', (d) => () => this.arcPath(d.current))

    g.select('path.label-path')
      .attrTween('d', (d) => () => this.textPositionPath(d.current))

    g.select('text')
      .attr('fill-opacity', (d) => this.fontOpacity(d))

    // Change the stroke-width with no transition
    svg.selectAll('g').select('path.arc-path')
      .attr('stroke-width', (d) => d.storyPath === this.currentStoryPath ? this.borderWidth : 0)

    t.on('end', () => {
      svg.selectAll('g').select('text')
        .filter((d) => d.target.display)
        .style('font-size', d => this.largeArc(d.current) ? this.textFontSize(d) : 0)
    })
  }

  calculateTarget () {
    // Get the parent node
    let p = this.partitionData.find((d) => d.storyPath === this.currentStoryPath)
    if (p) {
      if (!p.children) {
        // If no children, must be at max level - zoom to parent level only
        p = p.parent
      }
    } else {
      p = {
        x0: -1,
        x1: -1,
        y0: 0,
        y1: 0,
      }
    }

    // Determine how large to draw each arc
    const totalArcs = this.rootHeight + 1 // +1 for central arc
    const potentialArcs = totalArcs - p.y0 // ignore arcs before parent
    const maxArcs = this.maximum + 1 // +1 for central arc
    const numArcsToDisplay = Math.min(maxArcs, potentialArcs)
    const rArc = rTotal / numArcsToDisplay

    // Calculate target positions based upon parent p
    this.partitionData.each(d => {
      // x0 and x1 before scaled up to 2 pi
      const x0 = Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0)))
      const x1 = Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0)))
      const showArc = d.y1 - p.y0 <= maxArcs
      const invisibleSegment = (x0 === 0 && x1 === 0) || (x0 === 1 && x1 === 1)
      d.target = {
        x0: x0 * 2 * Math.PI,
        x1: x1 * 2 * Math.PI,
        y0: Math.max(0, d.y0 - p.y0),
        y1: Math.max(0, d.y1 - p.y0),
        innerRadius: Math.max(0, d.y0 - p.y0) * rArc,
        outerRadius: Math.max(0, d.y1 - p.y0) * rArc,
        display: showArc && !invisibleSegment,
      }
    })
  }

  color (d) {
    if (d.storyPath === PATH_ROOT) {
      return this.colour(PATH_ROOT)
    }
    if (d.children) {
      return this.color(d.children[0])
    }
    return d.data.colour
  }

  fontColor (d) {
    if (d.children) {
      return this.fontColor(d.children[0])
    }
    return this.tabFontColor(d.data.level1, StoryKeys.SIDEBAR_BOT)
  }

  fontOpacity (d) {
    return d.target.display ? 1 : 0
  }

  idFromStoryPath (storyPath: string) {
    return storyPath.replace(/\W+/g, '')
  }

  largeArc (d) {
    return (d.y1 - d.y0) * (d.x1 - d.x0) > largeArcConstant
  }

  text (d) {
    if (d.height === 0) {
      // level 3
      return d.data.data[this.projectLabelField]
    } else {
      return this.label(d.storyPath) || this.name(d.storyPath)
    }
  }

  updateTabLabels () {
    this.tabLabels.forEach(({ storyPath }) => {
      const id = this.idFromStoryPath(storyPath)
      select(`#${id}`).select('text').select('textPath')
        .text(d => this.textRestrictedLength(d))
    })
  }

  storyPathFromD (d) {
    if (d.depth === 3) {
      return d.data.path.toString()
    } else if (d.depth === 2) {
      return d.children[0].data.level2
    } else if (d.depth === 1) {
      return d.children[0].children[0].data.level1
    } else {
      return PATH_ROOT
    }
  }

  fillOpacity (d) {
    if (!d.target.display) {
      return 0
    }

    switch (d.depth) {
      case 0:
        return 1
      case 1:
        return 0.9
      case 2:
        return 0.75
      case 3:
        return 0.5
      default:
        return 0
    }
  }

  textPositionPath (d) {
    const midPoint = (d.x0 + d.x1) / 2 / Math.PI * 180 // in degrees
    const reverse = midPoint > 90 && midPoint < 270
    // Reverse the arc direction (and thus text direction) if the midpoint is on the lower half of the sunburst
    const r = arc(d)
      .startAngle((d) => reverse ? d.x1 : d.x0) // only modify the changed attributes
      .endAngle((d) => reverse ? d.x0 : d.x1) // only modify the changed attributes
      .padAngle((d) => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius * 1.5)
      .outerRadius((d) => (d.innerRadius + d.outerRadius) / 2)
      .innerRadius(undefined)

    return r(d).replace(/LNaN(,|\s)NaNZ/g, '')
  }

  arcPath (d) {
    // Function to build the arc segments
    const r = arc(d)
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .padAngle((d) => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius * 1.5)
      .innerRadius((d) => d.innerRadius || (d.y0 * radius))
      .outerRadius((d) => d.outerRadius || (d.y1 * radius))

    return r(d)
  }

  textFontSize (d) {
    const g = document.querySelector(`g#${d.id}`)
    const textElement = g.querySelector('text')
    const oldFontSize = textElement.style.fontSize
    textElement.style.fontSize = '1em' // Set to 1 to avoid issue on zoom
    const textPathElement = textElement.querySelector('textPath')
    const textWidth = textPathElement.getComputedTextLength()
    textElement.style.fontSize = oldFontSize // revert to previous

    const arcElement = g.querySelector('path.label-path')
    const arcWidth = arcElement.getTotalLength() - padding

    const fontSize = 1 + (arcWidth - textWidth) / textWidth
    const restrictedFontSize = Math.max(minFontSize, Math.min(fontSize, maxFontSize))
    return restrictedFontSize === minFontSize ? '0em' : restrictedFontSize + 'em'
  }

  textRestrictedLength (d) {
    const text = this.text(d)
    return text.length < maxChars ? text : (text.slice(0, maxChars) + '...')
  }

  storyPathUp () {
    const storyPathParts = this.currentStoryPath.split('/')
    let newStoryPath
    if (storyPathParts.length === 1) {
      newStoryPath = PATH_ROOT
    } else {
      newStoryPath = storyPathParts.slice(0, -1).join('/')
    }
    this.$typedStore.activeVisualisation.updateStoryPath(newStoryPath)
  }
}

</script>

<style>

#sunburst {
  width: 100%;
  height: 100%;
}

.cursor-pointer {
  cursor: pointer;
}

.pointer-events-none {
  pointer-events: none;
}

.opacity-0 {
  opacity: 0;
}

</style>

<style scoped>
.sunburst-container {
  position: relative;
  /* 15px is the offset number in
  SideTab.vue of the active tab */
  margin-left: -15px;
}

</style>
